import { colors } from '@lmc/common/constants'
import { Chevron } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import MapIcon from '@lmc/ui-kit/src/general/Icon/customShapes/Map'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import CONSTANTS from 'utils/constants'
import {
  GOOGLE_MAPS_EMBED_V1_PLACE_BASE_URL,
  staticMapUrl
} from 'utils/googleMapsApiUrls'

type Coordinates = { lat?: number; lng?: number }

type GeoLocationProps = {
  latitude?: Coordinates['lat']
  longitude?: Coordinates['lng']
  accuracy?: number
}

export const GeoLocation = ({
  latitude,
  longitude,
  accuracy
}: GeoLocationProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const isLocationAvailable = latitude && longitude

  const helpLink =
    'https://support.logmycare.co.uk/en/articles/6418064-why-can-t-i-see-gps-locations-on-some-logs'

  const embedUrl = isLocationAvailable
    ? `${GOOGLE_MAPS_EMBED_V1_PLACE_BASE_URL}?key=${CONSTANTS.VITE_REACT_APP_GOOGLE_MAPS_API_KEY}&q=${latitude},${longitude}&zoom=19`
    : ''

  const staticMap = isLocationAvailable ? staticMapUrl(latitude, longitude) : ''

  return (
    <Wrapper>
      {!isLocationAvailable && (
        <LoggedByContainer>
          {MapIcon}
          <UnavailableText>
            {t(incidentReportsConstants.UNAVAILABLE)}
          </UnavailableText>
          <Dot>•</Dot>
          <LocationInfoContainer>
            <HelpLink
              target="_blank"
              href={helpLink}
              rel="noreferrer"
              data-testid="location-unavailable-link"
            >
              {t(incidentReportsConstants.WHY_CANT_I_SEE_THIS)}{' '}
              <Chevron direction="right" />
            </HelpLink>
          </LocationInfoContainer>
        </LoggedByContainer>
      )}
      {isLocationAvailable && (
        <MapContainer>
          {accuracy && (
            <AccuracyNote>
              {t(incidentReportsConstants.LOCATION_ACCURACY, {
                accuracy: Math.round(accuracy)
              })}
            </AccuracyNote>
          )}
          <MapIframe
            title="Location Map"
            data-testid="google-map"
            width="100%"
            height="320"
            src={embedUrl}
            allowFullScreen
          />
          <img
            className="pdf-static-map"
            data-src={staticMap}
            alt=""
            style={{
              display: 'none',
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
        </MapContainer>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const MapContainer = styled.div`
  height: 320px;
  width: 100%;
  padding: 8px 0 24px;
`

const UnavailableText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

const LoggedByContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 12px;
`

const Dot = styled(UnavailableText)``

const LocationInfoContainer = styled.div``

const HelpLink = styled.a`
  display: flex;
  align-items: center;
  color: ${colors.primary};
  text-decoration: none;
`

const MapIframe = styled.iframe`
  border: none;
  width: 100%;
  height: 100%;
`

const AccuracyNote = styled(Text)`
  margin-top: 8px;
  color: ${(props) => props.theme.color.neutral.color5};
  font-size: 14px;
  text-align: center;
`

export default GeoLocation
